import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  contributeOptionsAnalytics,
  trackContributeContinue,
  type ContributeVenueContext,
} from "@/analytics/contributeAnalytics";
import {
  PoiContributionCard,
  type ContributionAction,
} from "@/components/Map/PoiContributionCard";

type Props = {
  /** `entry_point` for `contribute_options_viewed`: where Contribute was pressed. */
  entryPoint: string;
  /** The place the picked option's `*_cta_activated` event is about. */
  analyticsVenue: ContributeVenueContext;
  /** True while a venue is being created before navigation. */
  creating?: boolean;
  onSelectAction: (action: ContributionAction) => void;
  onClose: () => void;
  onBack?: () => void;
  /** Only the map's flow has a step to go back to. */
  onGetNotified?: () => void;
  onShowOptions?: () => void;
};

const noop = () => {};

/**
 * The "What you can do" picker: centred modal on desktop, full-screen on mobile.
 * Shared by the map's POI flow and the venue card's "Add info".
 */
export function ContributionOptionsModal({
  entryPoint,
  analyticsVenue,
  creating = false,
  onSelectAction: selectAction,
  onClose: close,
  onBack: back,
  onGetNotified = noop,
  onShowOptions = noop,
}: Props) {
  // Mounted once per opening.
  useEffect(() => {
    contributeOptionsAnalytics.track("page_view_page", {
      entry_point: entryPoint,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving without picking — the ✕, the backdrop, or the map flow's Back —
  // is the drop-off. A pick unmounts the modal too, so it is flagged first.
  const hasPicked = useRef(false);
  const onSelectAction = (action: ContributionAction) => {
    hasPicked.current = true;
    trackContributeContinue(analyticsVenue);
    selectAction(action);
  };
  const onClose = () => {
    if (!hasPicked.current) contributeOptionsAnalytics.track("back_icon", {});
    close();
  };
  const onBack = back
    ? () => {
        contributeOptionsAnalytics.track("back_icon", {});
        back();
      }
    : undefined;

  const card = (variant: "card" | "page") => (
    <PoiContributionCard
      step="options"
      creating={creating}
      variant={variant}
      onGetNotified={onGetNotified}
      onShowOptions={onShowOptions}
      onSelectAction={onSelectAction}
      onClose={onClose}
      onBack={onBack}
    />
  );

  // The venue card hosts this inside its own clickable root, so nothing may
  // bubble out of the modal.
  const contain = {
    onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
  };

  // Portaled to document.body: several callers (e.g. the map marker's
  // VenueCard popup) sit inside an ancestor with a CSS `transform`, which
  // creates a new containing block for `position: fixed` descendants. Left
  // un-portaled, "fixed inset-0" ends up pinned to that ancestor instead of
  // the viewport, cropping the overlay instead of centering it on screen.
  return createPortal(
    <>
      <div
        className="hidden lg:flex fixed inset-0 z-[1100] items-center justify-center bg-black/40 p-4"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        {...contain}
      >
        <div className="drop-shadow-xl" onClick={(e) => e.stopPropagation()}>
          {card("card")}
        </div>
      </div>

      <div
        className="lg:hidden fixed inset-0 z-[1100] bg-bg-brand-secondary-default"
        onClick={(e) => e.stopPropagation()}
        {...contain}
      >
        {card("page")}
      </div>
    </>,
    document.body,
  );
}

export default ContributionOptionsModal;
